import AbstractLoginModal, {
  AbstractLoginModalTemplateSpec,
  LoginFlow,
} from '../layout/AbstractLoginModal';
import { AppData, Colors, Lightning } from '@lightningjs/sdk';
import { translate } from 'support/translate';
import EmailInputContent from './EmailInputContent';
import { WidgetNames } from 'config/routes';
import { EmailKeyboard } from 'components/common/input/KeyboardConfiguration';
import TextLabel from 'components/common/TextLabel';

export interface EmailInputModalTemplateSpec
  extends AbstractLoginModalTemplateSpec {
  Content: {
    Heading: typeof TextLabel;
    Frame: object;
  } & AbstractLoginModalTemplateSpec['Content'];
}

const HEADING_SPACING = 44;
const HEADING_HEIGHT = 45;
const HEADING_FONT_SIZE = 28;

const INNER_CONTENT = 586;
const CONTENT_HEIGHT = INNER_CONTENT + HEADING_HEIGHT + HEADING_SPACING;
const CONTENT_OFFSET_Y = 229;

export default class EmailInputModal
  extends AbstractLoginModal
  implements
    Lightning.Component.ImplementTemplateSpec<EmailInputModalTemplateSpec>
{
  override _showBackButton = false;

  override get loginFlow() {
    return LoginFlow.GET_STARTED;
  }

  private _Heading = this._Content.getByRef('Heading')!;
  private _Frame = this._Content.getByRef('Frame')!;

  get announce() {
    return [this._Heading.text?.text];
  }

  static override _template(): Lightning.Component.Template<EmailInputModalTemplateSpec> {
    const { Content, ...rest } = super._template();
    return {
      Content: {
        ...Content,
        y: CONTENT_OFFSET_Y,
        h: CONTENT_HEIGHT,
        Heading: {
          type: TextLabel,
          text: {
            lineHeight: HEADING_HEIGHT,
            text: translate('account.signin.heading'),
            fontSize: HEADING_FONT_SIZE,
          },
        },
        Frame: {
          type: EmailInputContent,
          flexItem: { marginTop: HEADING_SPACING },
          h: INNER_CONTENT,
          w: Content!.w,
          rect: true,
          color: Colors('loginBackground').get(),
          signals: {
            $onEmailInputHovered: '$onEmailInputHovered',
            $onEmailSubmit: '$onEmailSubmit',
            $openKeyboard: '$openKeyboard',
          },
        },
      },
      ...rest,
    };
  }

  modalInit() {
    this._Keyboard.patch({
      keyboardConfiguration: EmailKeyboard,
    });
  }

  override _getFocused() {
    return this._Frame;
  }

  private async $onEmailSubmit(email: string) {
    const hasSignedUp = await this.onLoading(
      AppData!.loginRadiusService!.checkEmailAvailability(email),
    );
    if (hasSignedUp === undefined) return;

    if (hasSignedUp) {
      AppData?.modalService.open(WidgetNames.SIGN_IN_MODAL, {
        data: { email },
      });
    } else {
      AppData?.modalService.open(WidgetNames.SIGN_UP_MODAL, {
        data: { email },
      });
    }
  }

  private $onEmailInputHovered() {
    this._setState('Content');
  }
}
